(() => {
  'use strict';

  // CONFIGURATION
  const PROMO_ENABLED = true;
  const PROMO_DEBUG = false;
  const PROMO_VARIANT_ID = '51378136809813'; // Variante « Café de la Brasserie ».
  const PROMO_TRIGGER_QUANTITY = 2;
  const PROMO_FINAL_QUANTITY = 3;

  const STORAGE_KEY = `brasserie-2plus1-suppressed-${PROMO_VARIANT_ID}`;
  const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(?:\.js)?(?:[?#]|$)/;
  const nativeFetch = window.fetch.bind(window);
  let promoUpdateInProgress = false;
  let checkTimer = null;
  let lastKnownQuantity = null;

  const log = (...args) => {
    if (PROMO_DEBUG) console.log('[Brasserie 2+1]', ...args);
  };

  const configuredVariantId = () => {
    const id = String(PROMO_VARIANT_ID).trim();
    return /^\d+$/.test(id) && id !== '0' ? id : null;
  };

  const route = (name, fallback) =>
    window.routes && typeof window.routes[name] === 'string'
      ? window.routes[name]
      : fallback;

  const cartSections = () => {
    const ids = ['cart-drawer', 'cart-icon-bubble'];
    const itemsId = document.querySelector('cart-items')?.dataset.id;
    const footerId = document.querySelector('#main-cart-footer')?.dataset.id;
    if (itemsId) ids.push(itemsId);
    if (footerId) ids.push(footerId);
    return [...new Set(ids)];
  };

  const readSuppression = () => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === 'true';
    } catch (_error) {
      return false;
    }
  };

  const writeSuppression = (suppressed) => {
    try {
      if (suppressed) sessionStorage.setItem(STORAGE_KEY, 'true');
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (_error) {
      // sessionStorage peut être indisponible en navigation privée stricte.
    }
  };

  // FETCH CART
  const fetchCart = async () => {
    const response = await nativeFetch(route('cart_url', '/cart.js'), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) throw new Error(`Lecture du panier impossible (${response.status})`);
    return response.json();
  };

  const promoQuantity = (cart, variantId) =>
    cart.items
      .filter((item) => String(item.variant_id || item.id) === variantId)
      .reduce((total, item) => total + item.quantity, 0);

  const promoLine = (cart, variantId) =>
    cart.items.find((item) => String(item.variant_id || item.id) === variantId);

  // NOTIFICATION
  const showNotification = () => {
    let notice = document.getElementById('BrasseriePromoNotice');

    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'BrasseriePromoNotice';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      notice.setAttribute('aria-atomic', 'true');
      notice.innerHTML =
        '<strong>🎁 Votre paquet gratuit a été ajouté !</strong>' +
        '<span>Offre web : 2 achetés + 1 offert</span>';
      Object.assign(notice.style, {
        position: 'fixed',
        zIndex: '2147483640',
        right: 'max(1.6rem, env(safe-area-inset-right))',
        bottom: 'max(1.6rem, env(safe-area-inset-bottom))',
        maxWidth: 'min(36rem, calc(100vw - 3.2rem))',
        padding: '1.2rem 1.6rem',
        borderRadius: '0.8rem',
        background: 'rgb(var(--color-background, 255 255 255))',
        color: 'rgb(var(--color-foreground, 18 18 18))',
        border: '0.1rem solid rgba(var(--color-foreground, 18 18 18), 0.15)',
        boxShadow: '0 0.8rem 2.4rem rgba(0, 0, 0, 0.16)',
        lineHeight: '1.35',
        opacity: '0',
        transform: 'translateY(0.8rem)',
        transition: 'opacity 180ms ease, transform 180ms ease',
        pointerEvents: 'none',
      });
      notice.querySelector('strong').style.display = 'block';
      notice.querySelector('span').style.cssText = 'display:block;margin-top:.3rem;font-size:.9em;opacity:.75';
      document.body.appendChild(notice);
    }

    clearTimeout(showNotification.hideTimer);
    requestAnimationFrame(() => {
      notice.style.opacity = '1';
      notice.style.transform = 'translateY(0)';
    });
    showNotification.hideTimer = setTimeout(() => {
      notice.style.opacity = '0';
      notice.style.transform = 'translateY(0.8rem)';
    }, 4500);
  };

  const sectionInnerHtml = (html, selector) => {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.querySelector(selector)?.innerHTML ?? null;
  };

  // CART REFRESH
  const refreshCartUi = (cartState) => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents(cartState);
    } else if (cartState.sections?.['cart-drawer']) {
      const current = document.querySelector('#shopify-section-cart-drawer');
      const replacement = sectionInnerHtml(
        cartState.sections['cart-drawer'],
        '#shopify-section-cart-drawer'
      );
      if (current && replacement !== null) current.innerHTML = replacement;
    }

    const replacements = [['cart-icon-bubble', '#shopify-section-cart-icon-bubble']];
    const items = document.querySelector('cart-items');
    const footer = document.querySelector('#main-cart-footer');
    if (items?.dataset.id) replacements.push([items.dataset.id, '#main-cart-items']);
    if (footer?.dataset.id) replacements.push([footer.dataset.id, '#main-cart-footer']);

    replacements.forEach(([sectionId, selector]) => {
      const current = document.querySelector(selector);
      const html = cartState.sections?.[sectionId];
      if (!current || !html) return;
      const replacement = sectionInnerHtml(html, selector);
      if (replacement !== null) current.innerHTML = replacement;
    });

    document.dispatchEvent(
      new CustomEvent('brasserie:cart-updated', { detail: { cart: cartState } })
    );
  };

  // ADD FREE UNIT
  const setPromoQuantity = async (lineKey) => {
    const response = await nativeFetch(route('cart_change_url', '/cart/change.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineKey,
        quantity: PROMO_FINAL_QUANTITY,
        sections: cartSections(),
        sections_url: `${location.pathname}${location.search}`,
      }),
    });

    if (!response.ok) throw new Error(`Mise à jour du panier impossible (${response.status})`);
    return response.json();
  };

  // PROMO CHECK
  const checkPromotion = async () => {
    const variantId = configuredVariantId();
    if (!PROMO_ENABLED || !variantId || promoUpdateInProgress) return;

    promoUpdateInProgress = true;
    try {
      const cart = await fetchCart();
      const quantity = promoQuantity(cart, variantId);
      log('Quantité observée :', quantity, 'précédente :', lastKnownQuantity);

      // Un passage volontaire de 3 à 2 désactive la réinsertion pour cette session.
      if (lastKnownQuantity === PROMO_FINAL_QUANTITY && quantity === PROMO_TRIGGER_QUANTITY) {
        writeSuppression(true);
        lastKnownQuantity = quantity;
        log('Retrait manuel détecté : ajout automatique suspendu.');
        return;
      }

      // Une quantité autre que 2 réarme l'offre (par exemple 2 -> 1 -> 2).
      if (quantity !== PROMO_TRIGGER_QUANTITY) writeSuppression(false);

      if (quantity !== PROMO_TRIGGER_QUANTITY || readSuppression()) {
        lastKnownQuantity = quantity;
        return;
      }

      const line = promoLine(cart, variantId);
      if (!line) return;

      // Une variante sans propriétés forme normalement une seule ligne. Si plusieurs
      // lignes existent, ne rien modifier afin de ne pas dépasser 3 au total.
      if (line.quantity !== PROMO_TRIGGER_QUANTITY) {
        lastKnownQuantity = quantity;
        log('Plusieurs lignes détectées : aucune modification automatique.');
        return;
      }

      const updatedCart = await setPromoQuantity(line.key);
      lastKnownQuantity = PROMO_FINAL_QUANTITY;
      refreshCartUi(updatedCart);
      showNotification();
      log('Unité offerte ajoutée.');
    } catch (error) {
      if (PROMO_DEBUG) console.error('[Brasserie 2+1]', error);
    } finally {
      promoUpdateInProgress = false;
    }
  };

  const scheduleCheck = (delay = 120) => {
    clearTimeout(checkTimer);
    checkTimer = setTimeout(checkPromotion, delay);
  };

  // EVENT LISTENERS
  const productFormQuantities = new WeakMap();

  const announceEligibleQuantity = (form) => {
    if (!PROMO_ENABLED || !configuredVariantId()) return;
    const variantInput = form.querySelector('[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    if (!quantityInput || String(variantInput?.value) !== configuredVariantId()) return;

    const previous = productFormQuantities.get(form) ?? 1;
    const current = Number(quantityInput.value);

    // La sélection reste visiblement à 2 : on annonce seulement le cadeau.
    if (previous < PROMO_TRIGGER_QUANTITY && current === PROMO_TRIGGER_QUANTITY) {
      showNotification();
      log('Deux paquets sélectionnés : cadeau annoncé, quantité visible conservée à 2.');
    }

    productFormQuantities.set(form, current);
  };

  document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
    const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
    productFormQuantities.set(form, quantity);
  });

  document.addEventListener('click', (event) => {
    const form = event.target.closest?.('form[action*="/cart/add"]');
    if (form && event.target.closest?.('[data-quantity-plus], button[name="plus"]')) {
      setTimeout(() => announceEligibleQuantity(form), 0);
    }
  });

  document.addEventListener('change', (event) => {
    const form = event.target.closest?.('form[action*="/cart/add"]');
    if (form && event.target.matches('[name="quantity"]')) announceEligibleQuantity(form);
  });

  const prepareDirectCheckout = (form) => {
    if (!PROMO_ENABLED || !configuredVariantId() || readSuppression() || !form) return;

    const variantInput = form.querySelector('[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    if (
      String(variantInput?.value) === configuredVariantId() &&
      Number(quantityInput?.value) === PROMO_TRIGGER_QUANTITY
    ) {
      // Le client choisit 2 ; Shopify reçoit 3 uniquement au clic sur ACHETER.
      quantityInput.value = String(PROMO_FINAL_QUANTITY);
      quantityInput.setAttribute('value', String(PROMO_FINAL_QUANTITY));
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      lastKnownQuantity = PROMO_FINAL_QUANTITY;
      log('Checkout préparé : 2 paquets choisis + 1 paquet offert.');
    }
  };

  // Le bouton fixe mobile peut lancer le checkout sans soumettre normalement le formulaire.
  document.addEventListener(
    'click',
    (event) => {
      if (!event.target.closest?.('[data-sticky-add]')) return;
      const form = [...document.querySelectorAll('form[action*="/cart/add"]')].find(
        (candidate) =>
          String(candidate.querySelector('[name="id"]')?.value) === configuredVariantId()
      );
      prepareDirectCheckout(form);
    },
    true
  );

  // Le bloc produit personnalisé utilise data-direct-checkout et peut quitter la page
  // immédiatement. La quantité est donc corrigée avant son gestionnaire de soumission.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
      prepareDirectCheckout(form);
    },
    true
  );

  // Rise/Dawn utilise fetch pour les ajouts et changements AJAX. Ce relais attend la
  // fin de la requête native, puis relit le vrai panier sans observer le DOM.
  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);
    const requestUrl = String(args[0] instanceof Request ? args[0].url : args[0]);
    if (!promoUpdateInProgress && response.ok && CART_MUTATION_PATTERN.test(requestUrl)) {
      scheduleCheck();
    }
    return response;
  };

  document.addEventListener('DOMContentLoaded', () => scheduleCheck(0), { once: true });
  window.addEventListener('pageshow', () => scheduleCheck(0));
  document.addEventListener('shopify:section:load', () => scheduleCheck());
})();
